import { useState, useEffect, useCallback, ReactNode } from 'react'
import clsx from 'clsx'
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'

import useWindowSize from '@/hooks/useWindowSize'
import { sliceArrayIntoChunks } from '@/utils/functions/utils'

import { ReactComponent as RightNavigator } from 'public/assets/icons/RightNavigator.svg'
import { ReactComponent as LeftNavigator } from 'public/assets/icons/LeftNavigator.svg'

type PropType = {
  className?: string
  sliderClassName?: string
  options?: EmblaOptionsType
  autoScroll?: boolean
  slides: ReactNode[]
  navigationType?: 'dot' | 'arrow' | 'all'
  buttonConfig?: {
    prevButtonClassName?: string
    nextButtonClassName?: string
  }
}

const AUTO_SCROLL_TIMER = 6000 // in ms

// TODO: pause carousel on visibility change = hidden
const Carousel = (props: PropType): JSX.Element => {
  const { options, slides, buttonConfig, autoScroll = false } = props
  const { isDesktop } = useWindowSize()

  const [carouselRef, carousel] = useEmblaCarousel(
    {
      draggable: true,
      loop: true,
      align: 'start',
      inViewThreshold: 0.5,
      slidesToScroll: isDesktop ? 3 : 1,
      breakpoints: {
        '(max-width: 640px)': { align: 'center' }
      },
      ...options
    },
    autoScroll ? [Autoplay({ delay: AUTO_SCROLL_TIMER })] : []
  )

  const { navigationType } = props
  const [selectedIndex, setSelectedIndex] = useState(0)
  const desktopSlides = sliceArrayIntoChunks(slides, 3)

  const scrollPrev = useCallback(
    () => carousel && carousel.scrollPrev(),
    [carousel]
  )
  const scrollNext = useCallback(
    () => carousel && carousel.scrollNext(),
    [carousel]
  )
  const scrollTo = useCallback(
    (index: number) => carousel && carousel.scrollTo(index),
    [carousel]
  )

  const onSelect = useCallback(() => {
    if (!carousel) return
    setSelectedIndex(carousel.selectedScrollSnap())
  }, [carousel, setSelectedIndex])

  useEffect(() => {
    if (!carousel) return
    carousel.on('select', onSelect)
  }, [carousel, onSelect])

  const navArrowButtonClass =
    'absolute top-1/2 -translate-y-1/2 | w-10 h-10 | flex justify-center align-middle | z-10 bg-gray-90 | font-medium | opacity-60 | rounded-full cursor-pointer outline-none | p-2 | hidden md:block fill-white'
  return (
    <div
      className={clsx('relative overflow-hidden', props.className)}
      ref={carouselRef}
    >
      <div className="flex select-none">
        {slides.map((slide, index) => (
          <div className={clsx('min-w-fit', props.sliderClassName)} key={index}>
            {slide}
          </div>
        ))}
      </div>

      {/* dot indicator */}
      <div className="flex list-none justify-center mt-6">
        {isDesktop
          ? desktopSlides.length > 1 &&
            desktopSlides?.map((_, index) => (
              <button
                key={index}
                className={clsx(
                  'bg-blue-web-27 | rounded-full | w-2 h-2 lg:w-3 lg:h-3 | mx-2',
                  index === selectedIndex ? '!bg-blue-40' : ''
                )}
                onClick={() => scrollTo(index)}
              />
            ))
          : slides.length > 1 &&
            slides.map((_, index) => (
              <button
                key={index}
                className={clsx(
                  'bg-blue-web-27 | rounded-full | w-2 h-2 lg:w-3 lg:h-3 | mx-2',
                  index === selectedIndex ? '!bg-blue-40' : ''
                )}
                type="button"
                onClick={() => scrollTo(index)}
              />
            ))}
      </div>

      {navigationType === 'arrow' || navigationType === 'all' ? (
        <>
          <button
            className={clsx(
              buttonConfig?.prevButtonClassName,
              navArrowButtonClass,
              'left-0'
            )}
            onClick={() => scrollPrev()}
            type="button"
          >
            <LeftNavigator className="text-white mx-auto" />
          </button>
          <button
            className={clsx(
              buttonConfig?.nextButtonClassName,
              navArrowButtonClass,
              'right-0'
            )}
            onClick={() => scrollNext()}
          >
            <RightNavigator className="text-white mx-auto" />
          </button>
        </>
      ) : null}
    </div>
  )
}

export default Carousel
